import type { StateCreator } from 'zustand'
import type {
  MessageGiftSent,
  MessageToastMiddle,
  MessageUserLevelUp,
} from '../../types/lives/messages'
import type {
  NotifyGift,
  NotifyLuckEarned,
  NotifyRocketGameExploded,
} from '../../types/lives/notifies'

export type PositionReturnLuck = 'top' | 'left' | 'right'

interface State {
  notifyGiftSentQuantityList: Record<string, MessageGiftSent>
  notifyGiftSentReturnList: MessageToastMiddle | null
  notifyUserLevelUpList: MessageUserLevelUp[]
  notifyGiftLuckList: (NotifyLuckEarned & { position: PositionReturnLuck })[]
  notifyGiftListToastSend: NotifyGift[]
  notifyGiftListToastEarned: NotifyGift[]
  currentNotifyGiftListToast: NotifyGift | null
  notifyUserJoinedLive: number[]
  notifyRocketGame: NotifyRocketGameExploded[]
}

interface Actions {
  clearNotifyGift: () => void

  addNotifyGiftSentQuantity: (notifyInfo: MessageGiftSent) => void
  addNotifyGiftSentReturnList: (notifyInfo: MessageToastMiddle) => void
  addNotifyUserLevelUpList: (notifyInfo: MessageUserLevelUp) => void
  addNotifyGiftToastSend: (notifyInfo: NotifyGift) => void
  addNotifyGiftToastEarned: (notifyInfo: NotifyGift) => void
  addNotifyGiftLuck: (notifyInfo: NotifyLuckEarned) => void
  addNotifyUserJoinedLive: (id: number) => void
  addNotifyRocketGame: (rocketGameData: NotifyRocketGameExploded) => void

  removeNotifyGiftLuck: (id: string) => void
  removeNotifyGiftSentQuantity: (id: string) => void
  removeNotifyGiftToastSend: (notifyInfo: NotifyGift) => void
  removeNotifyGiftToastEarned: (notifyInfo: NotifyGift) => void
  removeNotifyGiftSentReturn: () => void
  removeNotifyUserLevelUp: (notifyInfo: MessageUserLevelUp) => void
  removeNotifyUserJoinedLive: (id: number) => void
  removeNotifyRocketGame: (id: string) => void

  clearNotifyUserJoinedLive: () => void
  clearGiftSentReturnList: () => void
  clearUserLevelUpList: () => void
  clearGiftLuck: () => void
  clearGiftSentQuantityList: () => void
  clearNotifyGiftToastEarned: () => void
  clearNotifyRocketGame: () => void
}

export type NotifyLiveSlice = State & Actions

export const notifyLiveStore: StateCreator<
  NotifyLiveSlice,
  [],
  [],
  NotifyLiveSlice
> = (set) => ({
  notifyGiftListToastSend: [],
  notifyGiftListToastEarned: [],
  notifyGiftSentQuantityList: {},
  notifyGiftSentReturnList: null,
  notifyUserLevelUpList: [],
  notifyUserJoinedLive: [],
  notifyGiftLuckList: [],
  notifyRocketGame: [],
  currentNotifyGiftListToast: null,
  addNotifyRocketGame: (notifyRocketGame) => {
    set((prevState) => {
      const rocketsGames = prevState.notifyRocketGame
      const isReward = 'typeReward' in notifyRocketGame

      // EXPLOSÃO (sem typeReward): notifica 1x por bomba (rocketGameLiveId),
      // independente do progresso — evita a mesma bomba estourada notificar várias
      // vezes (presente grande dispara múltiplos eventos). Recompensa (typeReward,
      // por gift coletado) NÃO é dedupada.
      if (!isReward) {
        const dup = rocketsGames.find(
          (r) =>
            !('typeReward' in r) &&
            r.rocketGameLiveId === notifyRocketGame.rocketGameLiveId,
        )
        if (dup) {
          return { notifyRocketGame: rocketsGames }
        }
      }

      return {
        notifyRocketGame: rocketsGames.concat({
          ...notifyRocketGame,
          id: String(Date.now()),
        }),
      }
    })
  },
  addNotifyGiftToastSend: (notifyInfo) => {
    set((prevState) => {
      const newToast =
        notifyInfo?.message?.topGlobal ??
        notifyInfo?.message?.topLocal ??
        notifyInfo?.message?.topSameCountry

      const currentToast =
        prevState.currentNotifyGiftListToast?.message?.topGlobal ??
        prevState.currentNotifyGiftListToast?.message?.topLocal ??
        prevState.currentNotifyGiftListToast?.message?.topSameCountry

      if (
        newToast?.isLuck &&
        currentToast?.isLuck &&
        currentToast?.userSentId === newToast?.userSentId &&
        prevState.currentNotifyGiftListToast
      ) {
        return {
          currentNotifyGiftListToast: {
            ...prevState.currentNotifyGiftListToast,
            message: notifyInfo.message,
          },
        }
      }

      if (!prevState.currentNotifyGiftListToast) {
        return {
          currentNotifyGiftListToast: notifyInfo,
        }
      }

      return {
        notifyGiftListToastSend:
          prevState.notifyGiftListToastSend.concat(notifyInfo),
      }
    })
  },
  addNotifyGiftSentQuantity: (notifyInfo) => {
    set((prevState) => {
      const list = { ...prevState.notifyGiftSentQuantityList }

      const giftName = notifyInfo?.gift?.name
      const id = `${notifyInfo?.userSentId}-${notifyInfo?.userReceivedId}-${giftName}`

      if (list[id]) {
        list[id].quantity += notifyInfo.quantity
      } else {
        list[id] = notifyInfo
        list[id].id = id
      }

      return {
        notifyGiftSentQuantityList: list,
      }
    })
  },
  addNotifyGiftSentReturnList: (notifyInfo) => {
    set({
      notifyGiftSentReturnList: notifyInfo,
    })
  },
  addNotifyUserLevelUpList: (notifyInfo) => {
    set((prevState) => ({
      notifyUserLevelUpList: prevState.notifyUserLevelUpList.concat(notifyInfo),
    }))
  },
  addNotifyGiftToastEarned: (notifyInfo) => {
    set((prevState) => {
      const newToast =
        notifyInfo?.message?.topGlobal ??
        notifyInfo?.message?.topLocal ??
        notifyInfo?.message?.topSameCountry

      const currentToast =
        prevState.currentNotifyGiftListToast?.message?.topGlobal ??
        prevState.currentNotifyGiftListToast?.message?.topLocal ??
        prevState.currentNotifyGiftListToast?.message?.topSameCountry

      if (
        newToast?.isLuck &&
        currentToast?.isLuck &&
        currentToast?.userSentId === newToast?.userSentId &&
        prevState.currentNotifyGiftListToast
      ) {
        return {
          currentNotifyGiftListToast: {
            ...prevState.currentNotifyGiftListToast,
            message: notifyInfo.message,
          },
        }
      }

      if (!prevState.currentNotifyGiftListToast) {
        return {
          currentNotifyGiftListToast: notifyInfo,
        }
      }

      return {
        notifyGiftListToastEarned:
          prevState.notifyGiftListToastEarned.concat(notifyInfo),
      }
    })
  },
  addNotifyUserJoinedLive: (id) => {
    const removeDuplicate = (array: number[]): number[] => {
      return array?.filter((i, index) => {
        return index === array.indexOf(i)
      })
    }

    set((prevState) => ({
      notifyUserJoinedLive: removeDuplicate(
        prevState.notifyUserJoinedLive,
      )?.concat(id),
    }))
  },
  addNotifyGiftLuck: (notifyInfo) => {
    set((prevState) => {
      const list = prevState.notifyGiftLuckList

      // só notifica se TEM retorno de verdade — descarta notificação vazia/sem valor
      // (evita o badge prata em branco "você ganhou ___ vezes")
      if (!(Number(notifyInfo?.returnValues) > 0) || !(Number(notifyInfo?.returnTimes) > 0)) {
        return {} as Partial<NotifyLiveSlice>
      }

      // dedup pelo id (evita badge repetido por re-render/mensagem dupla)
      if (list.some((n) => n.id === notifyInfo.id)) {
        return {} as Partial<NotifyLiveSlice>
      }

      // espalha nas 3 posições: escolhe a menos usada no momento
      const positions: PositionReturnLuck[] = ['top', 'left', 'right']
      const counts: Record<PositionReturnLuck, number> = { top: 0, left: 0, right: 0 }
      for (const n of list) counts[n.position]++
      const position = positions.reduce((a, b) => (counts[b] < counts[a] ? b : a), 'top' as PositionReturnLuck)

      // cada badge tem lifecycle próprio (remove por id ao terminar a animação);
      // cap nos últimos 9 pra não crescer sem limite num spam
      const next = [...list, { ...notifyInfo, position }].slice(-9)
      return { notifyGiftLuckList: next }
    })
  },
  removeNotifyRocketGame: (id) => {
    set((prevState) => ({
      notifyRocketGame: prevState.notifyRocketGame?.filter((info) => {
        return info?.id !== id
      }),
    }))
  },
  removeNotifyGiftToastSend: () => {
    set((prevState) => {
      const list = [...prevState.notifyGiftListToastSend]

      if (list?.length) {
        const [newNotifyInfo, ...listWithoutNewNotifyInfo] =
          prevState.notifyGiftListToastSend

        return {
          currentNotifyGiftListToast: newNotifyInfo,
          notifyGiftListToastSend: listWithoutNewNotifyInfo,
        }
      }

      return {
        currentNotifyGiftListToast: null,
      }
    })
  },
  removeNotifyGiftToastEarned: () => {
    set((prevState) => {
      const list = [...prevState.notifyGiftListToastEarned]

      if (list?.length) {
        const [newNotifyInfo, ...listWithoutNewNotifyInfo] =
          prevState.notifyGiftListToastEarned

        return {
          currentNotifyGiftListToast: newNotifyInfo,
          notifyGiftListToastEarned: listWithoutNewNotifyInfo,
        }
      }

      return {
        currentNotifyGiftListToast: null,
      }
    })
  },
  removeNotifyGiftSentQuantity: (id) => {
    set((prevState) => {
      const newObj = { ...prevState?.notifyGiftSentQuantityList }
      delete newObj[id]
      return { notifyGiftSentQuantityList: newObj }
    })
  },
  removeNotifyGiftSentReturn: () => {
    set({
      notifyGiftSentReturnList: null,
    })
  },
  removeNotifyUserLevelUp: (notifyInfo) => {
    set((prevState) => ({
      notifyUserLevelUpList: prevState.notifyUserLevelUpList?.filter((info) => {
        return info?.id !== notifyInfo?.id
      }),
    }))
  },
  removeNotifyUserJoinedLive: (id) => {
    set((prevState) => ({
      notifyUserJoinedLive: prevState.notifyUserJoinedLive?.filter((userId) => {
        return userId !== id
      }),
    }))
  },
  removeNotifyGiftLuck: (id) => {
    set((prevState) => ({
      notifyGiftLuckList: prevState.notifyGiftLuckList.filter((n) => n.id !== id),
    }))
  },
  clearNotifyRocketGame: () => {
    set({ notifyRocketGame: [] })
  },
  clearNotifyGift: () => {
    set({ notifyGiftListToastSend: [] })
  },
  clearUserLevelUpList: () => {
    set({ notifyUserLevelUpList: [] })
  },
  clearNotifyUserJoinedLive: () => {
    set({ notifyUserJoinedLive: [] })
  },
  clearGiftSentReturnList: () => {
    set({ notifyGiftSentReturnList: null })
  },
  clearGiftSentQuantityList: () => {
    set({ notifyGiftSentQuantityList: {} })
  },
  clearNotifyGiftToastEarned: () => {
    set({ notifyGiftListToastEarned: [] })
  },
  clearGiftLuck: () => {
    set({ notifyGiftLuckList: [] })
  },
})
